import { useCallback, useMemo, useRef, useState } from 'react';

import {
    DragEndEvent,
    DragStartEvent,
    KeyboardSensor,
    Modifier,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { IChecklistTask } from '@features/Checklist/model/types';

interface IUseChecklistDndProps {
    checklist: IChecklistTask[];
    onReorderChecklistItem: (activeClientId: string, overClientId: string) => void;
}

export const useChecklistDnd = ({
    checklist,
    onReorderChecklistItem,
}: IUseChecklistDndProps) => {
    const [isDraggingChecklist, setIsDraggingChecklist] = useState(false);
    const [activeChecklistItem, setActiveChecklistItem] = useState<IChecklistTask | null>(null);
    const checklistListRef = useRef<HTMLElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 6,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const restrictToChecklistBounds = useMemo<Modifier>(
        () =>
            ({ draggingNodeRect, transform }) => {
                const checklistListRect = checklistListRef.current?.getBoundingClientRect();

                if (!draggingNodeRect || !checklistListRect) {
                    return transform;
                }

                const nextTop = draggingNodeRect.top + transform.y;
                const nextBottom = draggingNodeRect.bottom + transform.y;

                if (nextTop < checklistListRect.top) {
                    return {
                        ...transform,
                        y: checklistListRect.top - draggingNodeRect.top,
                    };
                }

                if (nextBottom > checklistListRect.bottom) {
                    return {
                        ...transform,
                        y: checklistListRect.bottom - draggingNodeRect.bottom,
                    };
                }

                return transform;
            },
        []
    );

    const onDragStart = useCallback(
        ({ active }: DragStartEvent) => {
            setIsDraggingChecklist(true);
            setActiveChecklistItem(
                checklist.find((item) => item.clientId === String(active.id)) ?? null
            );
        },
        [checklist]
    );

    const resetDragState = useCallback(() => {
        setIsDraggingChecklist(false);
        setActiveChecklistItem(null);
    }, []);

    const onDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            resetDragState();

            if (!over || active.id === over.id) {
                return;
            }

            onReorderChecklistItem(String(active.id), String(over.id));
        },
        [onReorderChecklistItem, resetDragState]
    );

    return {
        sensors,
        checklistListRef,
        isDraggingChecklist,
        activeChecklistItem,
        restrictToChecklistBounds,
        onDragStart,
        onDragCancel: resetDragState,
        onDragEnd,
    };
};
